import { Observable } from 'rxjs';
import { Order } from '../entities/order.entity';
import { OrderAsset } from '../entities/order-asset.entity';
import { OrderDocument } from '../entities/order-document.entity';
import { OrderStatus } from '../value-objects/order-status.vo';
import { CreateOrderWithQualityControlDto } from '../../application/dtos/create-order-with-quality-control.dto';
import { EditOrderAssetItemDto, EditOrderAssetsDto } from '../../application/dtos/edit-order-assets.dto';
import { OrderBulkCreateResult, OrderBulkValidateResult } from '../entities/order-bulk.entity';

export interface OrderListFilter {
  code?: string;
  portfolioIds?: string[];
  clientIds?: string[];
  clientReference?: string;
  technicalUserIds?: string[];
  qualityControlStatus?: number[];
  qualityControlResults?: number[];
  createdAtFrom?: string;
  createdAtTo?: string;
  status?: number;
  lastVersionCreatedAtFrom?: string;
  lastVersionCreatedAtTo?: string;
  lastVerificationCreatedAtFrom?: string;
  lastVerificationCreatedAtTo?: string;
  qualityControlFinishedAtFrom?: string;
  qualityControlFinishedAtTo?: string;
  finishedAtFrom?: string;
  finishedAtTo?: string;
}

export interface OrderListPagedRequest {
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: OrderListFilter;
}

export interface OrderListPagedResponse {
  items: Order[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
  orderField?: string;
  ascendingOrder: boolean;
}

export interface OrderListByPortfolioFilter {
  code?: string;
  clientId?: string;
  clientReference?: string;
  status?: number;
  createdAtFrom?: string;
  createdAtTo?: string;
  lastVersionCreatedAtFrom?: string;
  lastVersionCreatedAtTo?: string;
  lastVerificationCreatedAtFrom?: string;
  lastVerificationCreatedAtTo?: string;
}

export interface OrderListByPortfolioRequest {
  orderField?: string;
  ascendingOrder: boolean;
  filter?: OrderListByPortfolioFilter;
  page: number;
  pageSize: number;
}

export interface OrderDocumentListPagedRequest {
  orderId: string;
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
}

export interface OrderDocumentListPagedResponse {
  items: OrderDocument[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

export interface OrderDocumentUpload {
  file: File;
  documentType: number;
}

export interface OrderAssetListFilter {
  propertyRegistryNumber?: string;
  propertyRegistry?: string;
  cadastralReference?: string;
  idufir?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export interface OrderAssetListPagedRequest {
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: OrderAssetListFilter;
}

export interface OrderAssetListPagedResponse {
  items: OrderAsset[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

export abstract class OrderRepositoryPort {
  abstract getListPaged(request: OrderListPagedRequest): Observable<OrderListPagedResponse>;
  abstract getListPagedByPortfolio(portfolioId: string, request: OrderListByPortfolioRequest): Observable<OrderListPagedResponse>;
  abstract getDetails(id: string): Observable<Order>;
  abstract create(
    clientId: string,
    clientReference?: string,
    portfolioId?: string,
    expectedDeliveryDate?: string,
  ): Observable<string>;
  abstract createWithQualityControl(dto: CreateOrderWithQualityControlDto): Observable<string>;
  abstract updateStatus(id: string, newStatus: OrderStatus): Observable<void>;
  abstract update(id: string, clientId: string, clientReference?: string, portfolioId?: string): Observable<void>;
  abstract getOrderDocuments(request: OrderDocumentListPagedRequest): Observable<OrderDocumentListPagedResponse>;
  abstract uploadOrderDocument(orderId: string, file: File, documentType: number): Observable<void>;
  abstract uploadOrderDocuments(orderId: string, documents: OrderDocumentUpload[]): Observable<void>;
  abstract deleteOrderDocument(documentId: string): Observable<void>;
  abstract getOrderAssets(orderId: string, request: OrderAssetListPagedRequest): Observable<OrderAssetListPagedResponse>;
  abstract editOrderAssets(orderId: string, dto: EditOrderAssetsDto): Observable<string[]>;
  abstract deleteOrderAsset(orderId: string, assetId: string): Observable<void>;
  abstract updateOrderAsset(orderId: string, assetId: string, dto: EditOrderAssetItemDto): Observable<void>;
  abstract cancelOrder(id: string): Observable<void>;
  abstract downloadBulkTemplate(): Observable<Blob>;
  abstract validateBulkOrders(excelFile: File): Observable<OrderBulkValidateResult>;
  abstract createBulkOrders(excelFile: File, documentFiles?: File[]): Observable<OrderBulkCreateResult>;
}
